/**
 * What Buddy took for granted (R55), as a plain list in a sunken box: under the outline, and under
 * the draft's summary, where the foot invites a correction. On a draft the exercises Buddy matched by
 * itself (R8) are assumptions too: each reads „written name → catalogue exercise" with „Zmień", which
 * opens the unresolved exercise's card for that name right under this box, and reads „Zamknij" while
 * the card is open.
 */

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { AiAutoMatch } from '~/entities/ai-conversation';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import styles from './Assumptions.module.css';
import surfaces from './surfaces.module.css';

/** Buddy's own picks on a draft, and which of them the trainer is changing now. */
export interface AssumedPicks {
  readonly matches: readonly AiAutoMatch[];
  /** The names whose card is open. */
  readonly open: ReadonlySet<string>;
  readonly onChange: (name: string) => void;
  /** The names whose decision is being saved: their open card cannot be closed until it lands. */
  readonly busy: ReadonlySet<string>;
  /** The id of a name's card; its „Zmień" is that id with `-change`. */
  readonly cardId: (name: string) => string;
}

interface AssumptionsProps {
  readonly items: readonly string[];
  /** The draft's invitation to correct it; the outline's box has none. */
  readonly foot?: string;
  /** The draft's automatic matches; the outline has none. */
  readonly picks?: AssumedPicks;
}

export function Assumptions({ items, foot, picks }: AssumptionsProps) {
  const { t } = useTranslation();
  const matches = picks?.matches ?? [];
  if (items.length === 0 && matches.length === 0) return null;
  return (
    <section className={styles.assumptions} aria-label={t('web:aiPlan.assumptions.title')}>
      <h4 className={styles.assumptionsTitle}>{t('web:aiPlan.assumptions.title')}</h4>
      <ul className={styles.assumptionList}>
        {picks !== undefined &&
          matches.map((match) => {
            const open = picks.open.has(match.name);
            const card = picks.cardId(match.name);
            // „Zamknij" refuses while the card's decision is being saved, as the card's own „Zostaw"
            // does: focusable, and saying why.
            const held = open && picks.busy.has(match.name);
            return (
              <li key={match.name} className={clsx(styles.assumption, styles.pick)}>
                <span className={styles.dot} aria-hidden="true" />
                <span>
                  <span aria-hidden="true">
                    {t('web:aiPlan.assumptions.pick', { name: match.name, exercise: match.exerciseName })}
                  </span>
                  <span className={hidden.srOnly}>
                    {t('web:aiPlan.assumptions.pickSpoken', { name: match.name, exercise: match.exerciseName })}
                  </span>
                </span>
                <button
                  type="button"
                  id={`${card}-change`}
                  className={clsx(surfaces.button, surfaces.small, surfaces.ghost)}
                  aria-label={t(open ? 'web:aiPlan.assumptions.closePick' : 'web:aiPlan.assumptions.changePick', { name: match.name })}
                  aria-expanded={open}
                  aria-controls={open ? card : undefined}
                  aria-disabled={held ? 'true' : undefined}
                  aria-describedby={held ? `${card}-busy` : undefined}
                  onClick={() => {
                    if (!held) picks.onChange(match.name);
                  }}
                >
                  {t(open ? 'web:aiPlan.assumptions.close' : 'web:aiPlan.unresolved.change')}
                </button>
                {held && (
                  <span id={`${card}-busy`} className={hidden.srOnly}>
                    {t('web:aiPlan.unresolved.saving')}
                  </span>
                )}
              </li>
            );
          })}
        {items.map((assumption, index) => (
          <li key={index} className={styles.assumption}>
            <span className={styles.dot} aria-hidden="true" />
            <span>{assumption}</span>
          </li>
        ))}
      </ul>
      {foot !== undefined && <p className={styles.assumptionsFoot}>{foot}</p>}
    </section>
  );
}
